"use client"

import React, { useRef, useState } from "react"
import { KeyboardAvoidingView, ScrollView } from "react-native"
import { FormattedDate, FormattedMessage, FormattedTime } from "react-intl"

import {
  B,
  Box,
  Button,
  H3,
  isWeb,
  P,
  ScrollBox,
  Sheet,
  Span,
  StarRating,
  TextInput,
  useRecordPostExperienceSurvey,
} from "@bullseye/components"
import type { Modal } from "@bullseye/types"

export type ExperienceSurvey = {
  experienceId: string
  title: string
  startDate: Date | string
  hostName: string
}

export type PostExperienceSurveyModalProps = {
  experiences: ExperienceSurvey[]
}

export const PostExperienceSurveyModal = ({
  isOpen,
  dismiss,
  experiences,
}: Modal<PostExperienceSurveyModalProps>) => {
  const safeExperiences = experiences || []
  const [page, setPage] = useState(0)
  // Store ratings and feedback keyed by experienceId
  const [responses, setResponses] = useState<
    Record<string, { rating: number; feedback: string }>
  >({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { recordPostExperienceSurvey } = useRecordPostExperienceSurvey()
  const ref = useRef<ScrollView>()

  const current = safeExperiences[page]
  const total = safeExperiences.length

  const handleRating = (rating: number) => {
    setResponses((prev) => ({
      ...prev,
      [current.experienceId]: {
        rating,
        feedback: prev[current.experienceId]?.feedback || "",
      },
    }))
  }

  const [showThankYou, setShowThankYou] = useState(false)

  const handleSubmit = async () => {
    setIsSubmitting(true)
    try {
      await recordPostExperienceSurvey(
        current.experienceId,
        responses[current.experienceId]?.rating || 0,
        responses[current.experienceId]?.feedback || "",
      )
      if (page < total - 1) {
        setPage(page + 1)
      } else {
        setShowThankYou(true)
      }
    } catch (error) {
      // TODO: Show error toast
      console.error("Failed to submit survey:", error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSkip = () => {
    if (page < total - 1) {
      setPage(page + 1)
    } else {
      setShowThankYou(true)
    }
  }

  if (showThankYou) {
    return (
      <Sheet
        show={isOpen}
        icon="x"
        onIconPress={() => dismiss()}
        dismiss={dismiss}
        disableSafeArea={isWeb()}
        renderContent={() => (
          <Box className="flex w-full flex-col items-center">
            <H3 className="px-1 text-2xl font-semibold text-gray-900">
              <FormattedMessage id="survey.experience.thankYou" />
            </H3>
            <P className="text-md mb-3 mt-4 !px-1 text-left !text-gray-900">
              <FormattedMessage id="survey.experience.subheading" />
            </P>
            <Button as="button" handlePress={dismiss} className="mt-4 w-full">
              <FormattedMessage id="survey.done" />
            </Button>
          </Box>
        )}
      />
    )
  }

  if (!current) return null

  const rating = responses[current.experienceId]?.rating || 0
  const feedback = responses[current.experienceId]?.feedback || ""

  const headingAlignment = isWeb() ? "text-left" : "text-center"
  const headingClassName = `${headingAlignment} px-1 text-2xl font-semibold text-gray-900`

  return (
    <Sheet
      show={isOpen}
      icon="x"
      onIconPress={() => dismiss()}
      dismiss={dismiss}
      disableSafeArea={isWeb()}
      renderContent={() => (
        <ScrollBox
          ref={ref}
          className="h-640 w-full"
          style={{ maxHeight: 640 }}
          scrollEnabled
          showsVerticalScrollIndicator={false}
        >
          <KeyboardAvoidingView
            behavior="position"
            keyboardVerticalOffset={170}
          >
            <Box className="items-left flex w-full flex-col g-1 ">
              <H3 className={headingClassName}>
                <FormattedMessage id="survey.experience.heading" />{" "}
                {total > 1 ? `(${page + 1}/${total})` : ""}
              </H3>
              <P className="text-md mb-3 mt-4 !px-1 text-left !text-gray-900">
                <FormattedMessage id="survey.experience.subheading" />
              </P>
              <Span className="px-1 text-xl text-gray-900">
                <B>{current.title}</B>
              </Span>
              <Span className="px-1 text-base text-gray-900">
                <FormattedDate
                  value={current.startDate}
                  month="long"
                  year="numeric"
                  day="numeric"
                />
                {" at "}
                <FormattedTime
                  value={current.startDate}
                  hour="numeric"
                  minute="numeric"
                />
              </Span>
              {current.hostName && (
                <Span className="mb-2 px-1 text-base text-gray-900">
                  Hosted by {current.hostName}
                </Span>
              )}
              <StarRating
                value={rating}
                onChange={handleRating}
                maxStars={4}
                className="mb-2"
                labels={["Poor", "Okay", "Good", "Excellent"]}
              />
              {rating > 0 && (
                <Box>
                  <Span className="mb-1 px-1 text-base text-gray-900">
                    <B>
                      <FormattedMessage id="survey.experience.question" />
                    </B>
                  </Span>
                  <TextInput
                    className="text-md mb-2 mt-2 min-h-[80px] w-full p-1 text-base"
                    value={feedback}
                    placeholder="Additional Feedback"
                    accessibilityLabel="Additional Feedback"
                    onChangeText={(text) =>
                      setResponses((prev) => ({
                        ...prev,
                        [current.experienceId]: {
                          rating: prev[current.experienceId]?.rating || 0,
                          feedback: text,
                        },
                      }))
                    }
                    editable={!isSubmitting}
                    blurOnSubmit
                    returnKeyType="done"
                    multiline
                    onBlur={() => {
                      return
                    }}
                    grow
                    defaultHeight={120}
                    maxHeight={9999}
                  />
                </Box>
              )}
              <Button
                as="button"
                handlePress={handleSubmit}
                disabled={rating === 0 || isSubmitting}
                className="mt-4"
              >
                {isSubmitting ? (
                  <FormattedMessage id="survey.submitting" />
                ) : (
                  <FormattedMessage id="survey.submit" />
                )}
              </Button>
              <Button
                as="button"
                handlePress={handleSkip}
                className="mt-3"
                variant="secondary"
              >
                <FormattedMessage id="survey.skip" />
              </Button>
            </Box>
          </KeyboardAvoidingView>
        </ScrollBox>
      )}
    />
  )
}
